'use client';

import classNames from 'classnames';
import { motion } from 'framer-motion';
import { ReactNode, useId, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography, {
  TypographyStyle,
} from '@/components/atoms/typography/typography';
import styles from './generic-accordion.module.scss';

export interface GenericAccordionProps {
  /** Additional info to display next to the headline */
  additionalInfo?: string;
  /** The content inside the accordion */
  children: ReactNode;
  /** The class name to apply */
  className?: string;
  /** The headline for the accordion */
  headline: string;
  /** The tag style for the headline */
  headlineTagStyle?: TypographyStyle;
  /** Whether the accordion is initially expanded */
  initialExpanded?: boolean;
  /** Whether to show bottom border */
  showBottomBorder?: boolean;
}

const GenericAccordion = ({
  additionalInfo,
  className,
  children,
  headline,
  headlineTagStyle = 'labelLarge',
  initialExpanded = false,
  showBottomBorder = false,
}: GenericAccordionProps) => {
  const [isActive, setIsActive] = useState(initialExpanded);
  const contentId = useId();
  const buttonId = useId();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsActive(!isActive);
    }
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isActive]: isActive,
        [styles.hasBottomBorder]: showBottomBorder,
      })}
    >
      <button
        aria-controls={contentId}
        aria-expanded={isActive}
        className={styles.itemHeader}
        id={buttonId}
        onClick={() => setIsActive(!isActive)}
        onKeyDown={handleKeyDown}
        type="button"
      >
        <div className={styles.headlineWrapper}>
          {headline && (
            <Typography className={styles.headline} tagStyle={headlineTagStyle}>
              {headline}
            </Typography>
          )}

          {additionalInfo && (
            <Typography tagStyle="bodySmall">{additionalInfo}</Typography>
          )}
        </div>

        <Icon
          aria-hidden="true"
          className={styles.itemHeaderIcon}
          name="ChevronDown"
          size={24}
        />
      </button>

      <motion.div
        animate={{
          height: isActive ? 'auto' : 0,
        }}
        aria-labelledby={buttonId}
        className={styles.itemContent}
        id={contentId}
        initial={{ height: initialExpanded ? 'auto' : 0 }}
        role="region"
        transition={{ duration: 0.5 }}
      >
        <div className={styles.itemContentInner}>{children}</div>
      </motion.div>
    </div>
  );
};

GenericAccordion.displayName = 'GenericAccordion';

export default GenericAccordion;
